import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">文章不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '蜜桃视频',
      url: 'https://mitao-video.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mitao-video.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mitao-video.com/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://mitao-video.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`蜜桃视频,${article.tags.join(',')},${article.categoryName}内容`}
        canonicalUrl={`https://mitao-video.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.image}
        ogType="article"
        schemaData={[articleSchema, breadcrumbSchema]}
      />

      <article className="pt-20">
        {/* Hero Image */}
        <div className="relative h-[300px] md:h-[450px] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={`蜜桃视频 - ${article.title}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-4 md:px-6 pb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-3">
              {article.categoryName}
            </span>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 leading-tight">
              {article.title}
            </h1>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-5 border-b border-background-200/70">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: article.categoryName, to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 py-6">
            <div className="flex items-center gap-2.5">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-500">{article.date} 发布</p>
              </div>
            </div>
            <div className="flex items-center gap-4 ml-auto text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-line"></i> {article.likes.toLocaleString()} 点赞
              </span>
            </div>
          </div>

          {/* Tags */}
          <div className="flex items-center gap-2 mb-8 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Article Content */}
          <div
            className="text-foreground-800 leading-relaxed mb-12 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Divider */}
          <div className="border-t border-background-200/70 pt-12 mb-12">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">蜜桃视频创作者</p>
                </div>
              </div>
              <button className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                关注作者
              </button>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-18 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden cursor-pointer border border-background-200/70 hover:border-primary-200 transition-colors"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        title={`蜜桃视频 - ${rel.title}`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{rel.author}</span>
                        <span>{rel.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </>
  );
}